<script>
    import { createEventDispatcher, onMount } from "svelte";

    import debounce from "lodash/debounce";

    import { SEARCH } from "../../Utils/utilConstants.svelte";

    import { apiMethods } from "../../Services/index.svelte";

    import {
        getAmountInCurrency,
        getFormatDate,
        getMinDateOfPayment,
        getTodayWithTime,
        isObjectEmpty,
        setInputFilter,
        showError,
        showException,
        splitPaymentAmount,
    } from "../../Utils/utilFunctions.svelte";

    import {
        Loader,
        DecimalInput,
        DateInput,
        NotificationModal,
    } from "./index.svelte";
    import InfoIcon from "./InfoIcon.svelte";

    export let isRecordPaymentModalOpen = false;
    export let route = "";
    export let paymentDetails = {};
    export let closeRecordPaymentModal = () => {};
    export let optionsArr = [];
    export let paymentsData = [];
    export let isDoctorRegisteredForGST = false;

    const dispatch = createEventDispatcher();

    let isPreview = false,
        isSearchingEstimateOrInvoice = false,
        estimateOrInvoiceResult = { id: 0 },
        isValidEstimateOrInvoice = true,
        isSubmittingPayment = false,
        isReceived = Boolean(paymentDetails.receivedDate),
        isAddGST = false,
        isBeforeGST = true,
        isPaidByConflictWithPaymentFor = false; // Quotes cannot be paid by Health Fund

    let __backupEstimateID__;

    let routePaidForOptions = [];

    let receiveDateRef;

    let hasInvoicesAvailable = false;

    let recordedPaymentReceivedDate;

    $: isFromEstimate = route.toLowerCase() == "estimate";
    $: isFromInvoice = route.toLowerCase() == "invoice";
    $: isFromPatientEncounterBillingTab =
        route.toLocaleLowerCase() == "patientencounterbillingpayment";
    $: isFromReceiptTable = route.toLowerCase() == "receipttable";

    $: showClosedOptions =
        isFromPatientEncounterBillingTab && optionsArr.length > 1;

    $: if (paymentDetails.paidBy == Number(2)) {
        // paymentDetails.relTo = Number(1);
        if (isFromEstimate && !__backupEstimateID__)
            __backupEstimateID__ = Number(paymentDetails.relId);
    } else if (isFromEstimate) {
        paymentDetails.relTo = Number(0);
        if (__backupEstimateID__)
            paymentDetails.relId = Number(__backupEstimateID__);
    } else if (isFromInvoice) paymentDetails.relTo = Number(1);

    $: isSubmitBtnDisabled = _checkSubmitBtnDisable(
        paymentDetails,
        estimateOrInvoiceResult,
        isReceived,
        isWriteOffPayment,
        isPaidByConflictWithPaymentFor,
    );

    $: if (isFromEstimate || isFromInvoice) _initEstimateOrInvoice();
    else if (isFromPatientEncounterBillingTab) _initPaymentsFromEncounterView();
    else if (isFromReceiptTable) _initEditPaymentDetails();

    $: isWriteOffPayment = paymentDetails.typeOfPayment == 7;

    $: if ([4, 5].includes(paymentDetails.status))
        paymentDetails.typeOfPayment = 2; // refund related status
    else if ([7].includes(paymentDetails.status))
        paymentDetails.typeOfPayment = 7; // Write-off related status

    $: if (isWriteOffPayment) _resetPropertiesForWriteOffPayment();

    $: isPaidByConflictWithPaymentFor =
        paymentDetails.paidBy == 2 && paymentDetails.relTo == 0;

    $: isPaymentReceivedOrRefunded =
        paymentDetails.isEditPayment &&
        [1, 2, 5].includes(paymentDetails.status);

    $: _initPaidForOptions(optionsArr);

    $: showBeforeAfterGSTDropdown = isAddGST && paymentDetails.amount;

    $: isProcessingFeeMoreThanPayment =
        paymentDetails?.processingFee > paymentDetails?.amount ||
        (paymentDetails?.processingFee && !paymentDetails.amount);

    $: firstPatientReceivedPayment = paymentsData?.length
        ? paymentsData.some((pd) => pd.status == 1 && pd.paidBy == 0)
            ? paymentsData.find((pd) => pd.status == 1 && pd.paidBy == 0)
            : {}
        : {};

    onMount(() => {
        if (!route) _updateInputField();

        if (paymentDetails && paymentDetails.isEditPayment) {
            isAddGST = paymentDetails.includesGST;
            isBeforeGST = true; // in case data not reset
            paymentDetails.amount =
                splitPaymentAmount(paymentDetails).amount +
                (isAddGST
                    ? Number(0)
                    : splitPaymentAmount(paymentDetails).processing);

            recordedPaymentReceivedDate = paymentDetails.receivedDate;
        }
    });

    const toPreview = () => (isPreview = true);

    const toEdit = () => (isPreview = false);

    const getEstimateOrInvoice = (isManualChange = true) => {
        if (isFromEstimate) {
            paymentDetails.relTo = Number(0);

            if (
                !estimateOrInvoiceResult ||
                isObjectEmpty(estimateOrInvoiceResult)
            )
                getEstimateById();
            return;
        } else if (isFromInvoice) {
            paymentDetails.relTo = Number(1);

            if (
                !estimateOrInvoiceResult ||
                isObjectEmpty(estimateOrInvoiceResult)
            )
                getInvoiceById();
            return;
        } else if (isFromPatientEncounterBillingTab) {
            if (isPaidByConflictWithPaymentFor && hasInvoicesAvailable) {
                const { relTo, relId } = routePaidForOptions.find(
                    (option) => option.relTo == Number(1),
                );

                paymentDetails.relTo = relTo;
                paymentDetails.relId = relId;
            }

            return;
        } else if (isFromReceiptTable) {
            paymentDetails.isEditPayment = true;

            if (paymentDetails.relTo == Number(0)) getEstimateById();
            else if (paymentDetails.relTo == Number(1)) getInvoiceById();

            return;
        }

        if (!showClosedOptions) {
            isValidEstimateOrInvoice = true;

            estimateOrInvoiceResult = { id: 0 };

            if (
                !paymentDetails.relId ||
                !/^[0-9-\s]+$/i.test(paymentDetails.relId)
            )
                return;

            isSearchingEstimateOrInvoice = true;

            handleFetchingEstimateOrInvoice();
        } else if (paymentDetails.relId) {
            isValidEstimateOrInvoice = true;
            estimateOrInvoiceResult = { id: 0 };

            if (paymentDetails.relTo == Number(0)) {
                if (!isManualChange)
                    paymentDetails.relId = optionsArr
                        .filter((option) =>
                            option.hasOwnProperty("estimateNumber"),
                        )
                        .sort((a, b) => b.id - a.id)[0].id;

                isSearchingEstimateOrInvoice = true;

                getEstimateById();
            } else {
                if (!isManualChange)
                    paymentDetails.relId = optionsArr
                        .filter((option) =>
                            option.hasOwnProperty("invoiceNumber"),
                        )
                        .sort((a, b) => b.id - a.id)[0].id;

                isSearchingEstimateOrInvoice = true;

                getInvoiceById();
            }
        }
    };

    const handleSelectTypeOfPayment = () => {
        // if (
        //     paymentDetails.typeOfPayment == Number(2) &&
        //     paymentDetails.receivedDate
        // )
        //     paymentDetails.receivedDate = new Date(
        //         Math.max(new Date(paymentDetails.receivedDate), new Date()),
        //     );

        // if type of payment changes, reset received checkbox and clear date input
        isReceived = false;
        paymentDetails.receivedDate = null;

        // when change paymentType to Refund from Record Payment process, sync payment method to the option came from the  first received payment from patient
        if (
            paymentDetails.typeOfPayment == 2 &&
            !paymentDetails.isEditPayment &&
            isFromPatientEncounterBillingTab &&
            !isObjectEmpty(firstPatientReceivedPayment)
        )
            paymentDetails.paymentMethod =
                firstPatientReceivedPayment.paymentMethod;
    };

    const handleCloseOptionsSelect = () => {
        isValidEstimateOrInvoice = true;

        if (paymentDetails.relTo == Number(0)) getEstimateById();
        else getInvoiceById();
    };

    const handleFetchingEstimateOrInvoice = debounce(() => {
        if (paymentDetails.patientId) delete paymentDetails.patientId;
        if (paymentDetails.doctorId) delete paymentDetails.doctorId;
        if (paymentDetails.patientEncounterId) delete paymentDetails.patientId;

        if (isFromEstimate || paymentDetails.relTo == Number(0))
            getEstimateById();
        else if (isFromInvoice || paymentDetails.relTo == Number(1))
            getInvoiceById();
    }, SEARCH.DEBOUNCE_TIME);

    const getEstimateById = async () => {
        await apiMethods.getMethod
            .getEstimateById(paymentDetails.relId)
            .then((res) => {
                if (res.id) {
                    estimateOrInvoiceResult = { ...res };
                    if (!isFromEstimate && !isFromInvoice) {
                        paymentDetails.patientId =
                            estimateOrInvoiceResult.patientId;
                        paymentDetails.doctorId =
                            estimateOrInvoiceResult.doctorId;
                        paymentDetails.patientEncounterId =
                            estimateOrInvoiceResult.patientEncounterId;
                    }

                    if (
                        paymentDetails?.paidBy == 0 &&
                        !paymentDetails.isEditPayment
                    )
                        paymentDetails.processingFee =
                            res.internalProcessingFee;
                } else isValidEstimateOrInvoice = false;
            })
            .catch((err) => showException(err));

        await syncAddGST();

        isSearchingEstimateOrInvoice = false;
    };

    const getInvoiceById = async () => {
        await apiMethods.getMethod
            .getInvoiceById(paymentDetails.relId)
            .then((res) => {
                if (res.id) {
                    estimateOrInvoiceResult = { ...res };
                    if (!isFromEstimate && !isFromInvoice) {
                        paymentDetails.patientId =
                            estimateOrInvoiceResult.patientId;
                        paymentDetails.doctorId =
                            estimateOrInvoiceResult.doctorId;
                        paymentDetails.patientEncounterId =
                            estimateOrInvoiceResult.patientEncounterId;
                    }

                    if (
                        paymentDetails?.paidBy == 0 &&
                        !paymentDetails.isEditPayment
                    )
                        paymentDetails.processingFee =
                            res.internalProcessingFee;
                } else isValidEstimateOrInvoice = false;
            })
            .catch((err) => showException(err));

        await syncAddGST();

        isSearchingEstimateOrInvoice = false;
    };

    const syncAddGST = async () => {
        if(!isDoctorRegisteredForGST) return false;
        const billingDataInsuranceType = await getEncounterInsuranceType(
            estimateOrInvoiceResult.patientEncounterId,
        );

        // Cosmetic, 3rd Party, Public in Private
        const defaultAddGSTInsuranceTypes = [15, 18, 30];

        isAddGST = paymentDetails?.isEditPayment
            ? paymentDetails.includesGST
            : defaultAddGSTInsuranceTypes.includes(billingDataInsuranceType);
    };

    const getEncounterInsuranceType = async (encounterId) => {
        let insuranceType = "";

        if (!encounterId) return insuranceType;

        await apiMethods.getMethod
            .getPatientEncounterById(encounterId)
            .then((res) => {
                if (!res || res.error)
                    showError(
                        res.error ??
                            "Failed to load patient encounter details, please refresh the page and try again and contact support if the issue persists.",
                    );
                else {
                    const { billingData } = res;

                    const targetBillingData = billingData.find(
                        (bd) =>
                            bd.id ==
                            estimateOrInvoiceResult.patientEncounterBillingDataId,
                    );

                    if (targetBillingData)
                        insuranceType = targetBillingData.insuranceType;
                }
            })
            .catch((err) => showError(err));

        return insuranceType;
    };

    const handleToggleReceived = (received = false) => {
        if (!received && paymentDetails.receivedDate) {
            paymentDetails.receivedDate = null;
            setTimeout(() => {
                receiveDateRef.refreshValue();
            }, 0);
        }
    };

    const handleReceiveDateInput = () => {
        if (paymentDetails.receivedDate) isReceived = true;
    };

    const submitPayment = async () => {
        paymentDetails.includesGST = isAddGST;

        // Used to be an input, now hide it and ALWAYS send as an empty string
        paymentDetails.approvalNumber = "";

        if (isReceived && paymentDetails.typeOfPayment == Number(2))
            paymentDetails.status = 5; // Refunded
        else if (paymentDetails.typeOfPayment == Number(2))
            paymentDetails.status = 4; // Pending Refund
        else if (paymentDetails.typeOfPayment == Number(7))
            paymentDetails.status = 7; // Write Off

        if (paymentDetails.paymentMethod == Number(0))
            paymentDetails.billingProvider = Number(100); // EFT -> Bank
        else if (paymentDetails.paymentMethod == Number(1))
            paymentDetails.billingProvider = Number(0); // Credit Card -> Stripe

        // prevent init processing fee not modified will miss property of processingFee
        if (
            !paymentDetails.hasOwnProperty("processingFee") ||
            !paymentDetails.processingFee
        )
            paymentDetails.processingFee = Number(0);

        isSubmittingPayment = true;

        if (isAddGST) {
            const amountRatio = isBeforeGST ? 1.1 : 1;

            paymentDetails.amount =
                paymentDetails.amount * amountRatio + paymentDetails.processingFee;
        } else paymentDetails.amount = paymentDetails.amount + paymentDetails.processingFee;

        if (!paymentDetails.isEditPayment)
            await apiMethods.postMethod
                .createPayment(paymentDetails)
                .then((res) => {
                    if (res.id) {
                        closeModal(!isFromEstimate && !isFromInvoice);
                        if (isFromEstimate || isFromInvoice)
                            dispatch("paymentRecorded");
                    } else showError(res.error);
                })
                .catch((err) => showException(err));
        else
            await apiMethods.putMethod
                .updatePayment(paymentDetails)
                .then((res) => {
                    if (res.id) {
                        closeModal(!isFromEstimate && !isFromInvoice);
                        if (isFromEstimate || isFromInvoice)
                            dispatch("paymentRecorded");
                    } else showError(res.error);
                })
                .catch((err) => showException(err));

        isAddGST = false;

        isBeforeGST = true;

        isSubmittingPayment = false;
    };

    const closeModal = (needReload = false) => {
        toEdit();

        paymentDetails = {
            relTo: isFromInvoice ? 1 : 0,
            relId:
                !isFromEstimate && !isFromInvoice ? null : paymentDetails.relId,
            expireDate: !isFromEstimate ? null : paymentDetails.expireDate,
            dueDate: !isFromInvoice ? null : paymentDetails.dueDate,
            billingDetails:
                !isFromEstimate && !isFromInvoice
                    ? null
                    : paymentDetails.billingDetails,
            paidBy: 0,
            paymentMethod: 0,
            billingProvider: 100,
        };

        if (!isFromEstimate && !isFromInvoice)
            estimateOrInvoiceResult = { id: 0 };

        isReceived = false;

        isAddGST = false;

        isBeforeGST = true;

        paymentDetails.typeOfPayment = Number(1);

        closeRecordPaymentModal(needReload, paymentDetails);
    };

    // HELPER FUNCTIONS
    const _updateInputField = () => {
        setInputFilter(
            document.getElementById("estimateOrInvoiceIdField"),
            function (value) {
                return value ? /^[0-9-\s]+$/i.test(value) : true;
            },
            "Must be an integer",
        );
    };

    const _checkSubmitBtnDisable = (
        paymentDetails,
        estimateOrInvoiceResult,
        isReceived,
        isWriteOffPayment,
        isPaidByConflictWithPaymentFor,
    ) => {
        if (isFromInvoice || isFromEstimate) _initEstimateOrInvoice();

        if (!estimateOrInvoiceResult?.id) {
            isValidEstimateOrInvoice = false;
            return true;
        } else {
            isValidEstimateOrInvoice = true;
        }

        if (estimateOrInvoiceResult.id && estimateOrInvoiceResult.status == 9) {
            return true;
        }

        if (isReceived) {
            if (isWriteOffPayment) return false;
            else if (!paymentDetails.receivedDate) return true;
            return false;
        }

        if (!paymentDetails.amount || paymentDetails.amount == 0) return true;

        if (
            paymentDetails.processingFee &&
            paymentDetails.processingFee > paymentDetails.amount
        )
            return true;

        if (isPaidByConflictWithPaymentFor) return true;

        return false;
    };

    const _initEstimateOrInvoice = () => {
        isValidEstimateOrInvoice = true;

        estimateOrInvoiceResult.id = paymentDetails.relId;
        estimateOrInvoiceResult.billingDetails = paymentDetails.billingDetails;
        if (isFromEstimate)
            estimateOrInvoiceResult.expireDate = paymentDetails.expireDate;
        if (isFromInvoice)
            estimateOrInvoiceResult.dueDate = paymentDetails.dueDate;
    };

    export const _initPaymentsFromEncounterView = async () => {
        // remove CANCELLED options
        optionsArr = optionsArr.filter((option) => option.status != 9);

        if (paymentDetails && paymentDetails.isEditPayment) {
            if (paymentDetails.relTo == 0) {
                estimateOrInvoiceResult = optionsArr.find(
                    (option) =>
                        option.hasOwnProperty("estimateNumber") &&
                        option.id == paymentDetails.relId,
                );
                await getEstimateById();
            } else if (paymentDetails.relTo == 1) {
                estimateOrInvoiceResult = optionsArr.find(
                    (option) =>
                        option.hasOwnProperty("invoiceNumber") &&
                        option.id == paymentDetails.relId,
                );
                await getInvoiceById();
            }

            if (estimateOrInvoiceResult.status == 9) {
                // Estimate or Invoice is CANCELLED
                // Show temp option with relId = 0 that allows to select other quotes/invoices
                paymentDetails.relId = 0;
            }
        } else if (optionsArr.length == 1) {
            estimateOrInvoiceResult = { ...optionsArr[0] };
            paymentDetails = {
                ...paymentDetails,
                relId: estimateOrInvoiceResult.id,
                patientId: estimateOrInvoiceResult.patientId,
                doctorId: estimateOrInvoiceResult.doctorId,
                patientEncounterId: estimateOrInvoiceResult.patientEncounterId,
            };
            if (estimateOrInvoiceResult.hasOwnProperty("estimateNumber"))
                paymentDetails.relTo = Number(0);
            else if (estimateOrInvoiceResult.hasOwnProperty("invoiceNumber"))
                paymentDetails.relTo = Number(1);

            syncAddGST();
        } else if (
            optionsArr.some((option) => option.hasOwnProperty("estimateNumber"))
        ) {
            const firstEstimate = optionsArr.filter((option) =>
                option.hasOwnProperty("estimateNumber"),
            )[0];
            paymentDetails = {
                ...paymentDetails,
                relTo: Number(0),
                relId: firstEstimate.id,
                patientId: firstEstimate.patientId,
                doctorId: firstEstimate.doctorId,
                patientEncounterId: firstEstimate.patientEncounterId,
            };
            getEstimateById();
        } else if (
            optionsArr.some((option) => option.hasOwnProperty("invoiceNumber"))
        ) {
            const firstInvoice = optionsArr.filter((option) =>
                option.hasOwnProperty("invoiceNumber"),
            )[0];
            paymentDetails = {
                ...paymentDetails,
                relTo: Number(1),
                relId: firstInvoice.id,
                patientId: firstInvoice.patientId,
                doctorId: firstInvoice.doctorId,
                patientEncounterId: firstInvoice.patientEncounterId,
            };
            getInvoiceById();
        }
    };

    const _initEditPaymentDetails = () => {
        getEstimateOrInvoice();

        // // Pending related status
        // if ([4, 5].includes(paymentDetails.status))
        //     paymentDetails.typeOfPayment = 2;

        // // Write-off related status
        // if ([7].includes(paymentDetails.status))
        //     paymentDetails.typeOfPayment = 7;
    };

    const _resetPropertiesForWriteOffPayment = () => {
        isAddGST = false;
        isBeforeGST = true;
        paymentDetails.processingFee = Number(0);
        paymentDetails.paymentMethod = Number(0);
        paymentDetails.transactionId = "";
        paymentDetails.approvalNumber = "";
        isReceived = true;
        paymentDetails.receivedDate = getTodayWithTime();
    };

    const _initPaidForOptions = (arr) => {
        routePaidForOptions = [];
        hasInvoicesAvailable = false;

        if (paymentDetails && paymentDetails.id) {
            const sourceItem = arr.find(
                (quoteOrInv) =>
                    quoteOrInv?.payments?.length &&
                    quoteOrInv.payments.some((p) => p.id === paymentDetails.id),
            );
            if (sourceItem && sourceItem.status == 9) {
                if (sourceItem.hasOwnProperty("estimateNumber")) {
                    routePaidForOptions.push({
                        relId: sourceItem.id,
                        relTo: Number(0),
                        value: `Quote ${sourceItem.estimateNumber}`,
                    });
                } else if (sourceItem.hasOwnProperty("invoiceNumber")) {
                    routePaidForOptions.push({
                        relId: sourceItem.id,
                        relTo: Number(1),
                        value: `Invoice ${sourceItem.invoiceNumber}`,
                    });
                    hasInvoicesAvailable = true;
                }
                return;
            }
        }
        arr.map((option) => {
            if (option.status == 9) return; // cancelled
            if (option.hasOwnProperty("estimateNumber")) {
                routePaidForOptions.push({
                    relId: option.id,
                    relTo: Number(0),
                    value: `Quote ${option.estimateNumber}`,
                });
            } else if (option.hasOwnProperty("invoiceNumber")) {
                routePaidForOptions.push({
                    relId: option.id,
                    relTo: Number(1),
                    value: `Invoice ${option.invoiceNumber}`,
                });
                hasInvoicesAvailable = true;
            }
        });
    };

    const _handleSelectPaidForOptions = (e) => {
        const { value } = e.target;

        const selectedRelTo = value.split("-")[0];
        const selectedRelId = value.split("-")[1];

        paymentDetails.relTo = Number(selectedRelTo);
        paymentDetails.relId = Number(selectedRelId);

        if (selectedRelTo == Number(0))
            estimateOrInvoiceResult = optionsArr.find(
                (option) =>
                    option.hasOwnProperty("estimateNumber") &&
                    option.id == selectedRelId,
            );
        else if (selectedRelTo == Number(1))
            estimateOrInvoiceResult = optionsArr.find(
                (option) =>
                    option.hasOwnProperty("invoiceNumber") &&
                    option.id == selectedRelId,
            );
    };
</script>

<NotificationModal
    isModalOpen={isRecordPaymentModalOpen}
    title={`${isPreview ? "Preview" : paymentDetails.isEditPayment ? "Edit" : "Record"} Payment`}
    cancelText="Cancel"
    closeModal={() => closeModal()}
    closeModalFunction={closeModal}
    showConfirmBtn={false}
    isConfirming={isSubmittingPayment}
    btnOptions={[
        {
            loading: isSubmittingPayment,
            isHidden: !isPreview,
            text: "Back to edit",
            function: () => toEdit(),
        },
        {
            loading: isSubmittingPayment,
            isHidden: !isPreview,
            text: isSubmittingPayment ? "Approving" : "Approve",
            function: () => submitPayment(),
        },
        {
            loading: isSubmitBtnDisabled,
            isHidden: isPreview,
            text: "Submit",
            function: () => toPreview(),
        },
    ]}
    noContentPadding
>
    <div
        class="flex flex-col gap-y-4 flex-1 overflow-y-auto overflow-x-hidden p-6"
    >
        <div class="flex gap-x-4">
            <p class="w-48">Type of Payment:</p>
            <select
                class="input input-bordered input-sm w-60"
                disabled={isPreview || paymentDetails.isEditPayment}
                bind:value={paymentDetails.typeOfPayment}
                on:change={handleSelectTypeOfPayment}
            >
                <option value={Number(1)} selected>Payment</option>
                <option value={Number(2)}>Refund</option>
                <option value={Number(7)}>Write-off</option>
            </select>
        </div>

        <div class="flex gap-x-4">
            <p class="w-48">Paid by:</p>
            <select
                class="input input-bordered input-sm w-60"
                disabled={isPreview}
                bind:value={paymentDetails.paidBy}
                on:change|preventDefault={() => getEstimateOrInvoice(false)}
            >
                <option value={Number(0)}>Patient</option>
                <option value={Number(2)}>Health Fund</option>
            </select>
        </div>

        {#if !route}
            <div class="flex gap-x-4">
                <p class="w-48">Payment for:</p>
                <select
                    class="input input-bordered input-sm w-60"
                    disabled={isFromEstimate ||
                        isFromInvoice ||
                        (isFromPatientEncounterBillingTab &&
                            optionsArr.length == 1) ||
                        isPreview}
                    bind:value={paymentDetails.relTo}
                    on:change|preventDefault={() => getEstimateOrInvoice(false)}
                >
                    {#if paymentDetails.paidBy == Number(2)}
                        <option value={Number(1)} selected={true}>
                            Invoice
                        </option>
                    {:else if isFromPatientEncounterBillingTab}
                        {#if optionsArr.some( (option) => option.hasOwnProperty("estimateNumber"), )}
                            <option
                                value={Number(0)}
                                selected={paymentDetails.relTo == Number(0)}
                            >
                                Quote
                            </option>
                        {/if}
                        {#if optionsArr.some( (option) => option.hasOwnProperty("invoiceNumber"), )}
                            <option
                                value={Number(1)}
                                selected={paymentDetails.relTo == Number(1)}
                            >
                                Invoice
                            </option>
                        {/if}
                    {:else}
                        <option
                            value={Number(0)}
                            selected={isFromEstimate ||
                                (!isFromEstimate && !isFromInvoice)}
                        >
                            Quote
                        </option>
                        <option value={Number(1)} selected={isFromInvoice}>
                            Invoice
                        </option>
                    {/if}
                </select>
                {#if isSearchingEstimateOrInvoice}
                    <Loader />
                {/if}
            </div>

            <div class="flex gap-x-4">
                <p class="w-48">
                    {paymentDetails.relTo == 0 ? "Quote" : "Invoice"}
                    ID:
                </p>
                {#if !showClosedOptions}
                    <input
                        type="text"
                        id="estimateOrInvoiceIdField"
                        class="input input-bordered input-sm w-60"
                        disabled={(isFromEstimate &&
                            paymentDetails.paidBy == Number(0)) ||
                            isFromInvoice ||
                            (isFromPatientEncounterBillingTab &&
                                optionsArr.length == 1) ||
                            isPreview}
                        bind:value={paymentDetails.relId}
                        on:input|preventDefault={() => getEstimateOrInvoice()}
                    />
                {:else}
                    <select
                        class="select select-bordered select-sm w-60"
                        disabled={isPreview}
                        bind:value={paymentDetails.relId}
                        on:change={handleCloseOptionsSelect}
                    >
                        <option value="" disabled>-</option>
                        {#each optionsArr as option}
                            {#if paymentDetails.relTo == 0}
                                {#if option.hasOwnProperty("estimateNumber")}
                                    <option value={option.id}>
                                        {option.id}
                                    </option>
                                {/if}
                            {:else if paymentDetails.relTo == 1}
                                {#if option.hasOwnProperty("invoiceNumber")}
                                    <option value={option.id}>
                                        {option.id}
                                    </option>
                                {/if}
                            {/if}
                        {/each}
                    </select>
                {/if}

                {#if isSearchingEstimateOrInvoice}
                    <Loader />
                {/if}
            </div>
        {:else if routePaidForOptions.length}
            <div class="flex gap-x-4">
                <p class="w-48">Payment for:</p>
                <select
                    class="input input-bordered input-sm w-60"
                    disabled={isPreview}
                    on:change={_handleSelectPaidForOptions}
                >
                    {#if estimateOrInvoiceResult?.status == 9}
                        <option value="" disabled selected />
                    {/if}
                    {#each routePaidForOptions as option}
                        <option
                            value={`${option.relTo}-${option.relId}`}
                            selected={`${option.relTo}-${option.relId}` ==
                                `${paymentDetails.relTo}-${paymentDetails.relId}`}
                        >
                            {option.value}
                        </option>
                    {/each}
                </select>
                {#if isSearchingEstimateOrInvoice}
                    <Loader />
                {/if}
            </div>
        {/if}

        {#if isPaidByConflictWithPaymentFor}
            <p class="text-secondary">
                Health fund payments can be recorded for invoices only
            </p>
        {/if}

        {#if isSearchingEstimateOrInvoice}
            <p>Loading {paymentDetails.relTo == 0 ? "Quote" : "Invoice"}</p>
        {:else if !isValidEstimateOrInvoice}
            <div class="text-error">
                Unable to find {paymentDetails.relTo == 0 ? "Quote" : "Invoice"}
            </div>
        {:else if !isSearchingEstimateOrInvoice && estimateOrInvoiceResult.id}
            {#if estimateOrInvoiceResult.status == 9}
                <div class="text-error">
                    {paymentDetails.relTo == 0 ? "Quote" : "Invoice"}
                    is CANCELLED
                </div>
            {:else if !route}
                <div class="flex flex-col gap-y-4">
                    <div class="flex gap-x-4">
                        <p class="w-48">Billing name:</p>
                        <p>
                            {estimateOrInvoiceResult.billingDetails?.name}
                        </p>
                    </div>

                    <div class="flex gap-x-4">
                        <p class="w-48">Gap:</p>
                        <p>
                            {getAmountInCurrency(
                                estimateOrInvoiceResult.actualGap,
                            )}
                        </p>
                    </div>

                    <div class="flex gap-x-4">
                        <p class="w-48">Rebate:</p>
                        <p>
                            {getAmountInCurrency(
                                estimateOrInvoiceResult.rebate,
                            )}
                        </p>
                    </div>

                    <div class="flex gap-x-4">
                        <p class="w-48">Total:</p>
                        <p>
                            {getAmountInCurrency(
                                estimateOrInvoiceResult.totalFee,
                            )}
                        </p>
                    </div>

                    <div class="flex gap-x-4">
                        <p class="w-48">
                            {paymentDetails.relTo == 0 ? "Expiry" : "Due"} date:
                        </p>
                        <p>
                            {paymentDetails.relTo == 0
                                ? estimateOrInvoiceResult.expireDate
                                    ? getFormatDate(
                                          estimateOrInvoiceResult.expireDate,
                                      )
                                    : "Missing Date"
                                : estimateOrInvoiceResult.dueDate
                                  ? getFormatDate(
                                        estimateOrInvoiceResult.dueDate,
                                    )
                                  : "Missing Date"}
                        </p>
                    </div>
                </div>
            {/if}
        {/if}

        <div class="flex gap-x-4">
            <div class="flex flex-col gap-y-1 w-48">
                <p class="w-full">
                    {#if paymentDetails.typeOfPayment == 2}
                        <InfoIcon tooltipClass="w-64" info={`This should include any processing fees paid by the patient that you wish to refund. Please note: if you refund non-refundable processing fees already collected by the credit card payment provider, this will result in a negative balance on the encounter.`}></InfoIcon>
                    {/if}
                    Amount:
                </p>
            </div>
            <div class="flex items-center w-60 gap-x-2">
                <DecimalInput
                    inputClass={showBeforeAfterGSTDropdown ? "w-24" : "w-60"}
                    isDisabled={isPreview}
                    bind:value={paymentDetails.amount}
                />

                {#if showBeforeAfterGSTDropdown}
                    <select
                        class="input input-bordered input-sm w-32"
                        disabled={isPreview}
                        bind:value={isBeforeGST}
                    >
                        <option value={true}>Before GST </option>
                        <option value={false}>After GST </option>
                    </select>
                {/if}
            </div>
        </div>

        {#if !isWriteOffPayment}
            <div class="flex gap-x-4 items-center">
                <p class="w-48 pt-1">Add GST:</p>

                <div class="flex items-center w-60">
                    <input
                        type="checkbox"
                        class="toggle toggle-secondary"
                        disabled={isPreview || !isDoctorRegisteredForGST}
                        bind:checked={isAddGST}
                    />

                    {#if isAddGST}
                        <p class="w-48 pl-2 pt-1 text-sm">
                            {paymentDetails.amount
                                ? isSubmittingPayment || !isBeforeGST
                                    ? getAmountInCurrency(paymentDetails.amount)
                                    : getAmountInCurrency(
                                          paymentDetails.amount * 1.1,
                                      )
                                : ""}
                        </p>
                    {/if}
                </div>
            </div>

            <div class="flex gap-x-4">
                <p class="w-48">
                    {#if paymentDetails.typeOfPayment == 2}
                        <InfoIcon tooltipClass="w-64" info={`This is for any processing fee added for the refund process, which will be subtracted from the amount refunded.`}></InfoIcon>
                    {/if}
                    Processing fee:
                </p>
                <DecimalInput
                    inputClass={isProcessingFeeMoreThanPayment
                        ? "w-60 input-error"
                        : "w-60"}
                    isDisabled={isPreview}
                    bind:value={paymentDetails.processingFee}
                />
            </div>

            {#if isProcessingFeeMoreThanPayment}
                <div class="text-error">
                    Processing fee cannot be more than payment amount
                </div>
            {/if}

            <div class="flex gap-x-4">
                <p class="w-48">Payment method:</p>
                <select
                    class="input input-bordered input-sm w-60"
                    disabled={isPreview}
                    bind:value={paymentDetails.paymentMethod}
                >
                    <option value={Number(0)}>EFT</option>
                    <option value={Number(1)}>Credit Card</option>
                </select>
            </div>

            <div class="flex gap-x-4">
                <p class="w-48">Transaction ID:</p>
                <input
                    type="text"
                    class="input input-bordered input-sm w-60"
                    disabled={isPreview}
                    bind:value={paymentDetails.transactionId}
                />
            </div>

            <!-- Hide and Always send as an empty string -->
            <!-- <div class="flex gap-x-4">
                <p class="w-48">Approval ID:</p>
                <input
                    type="text"
                    class="input input-bordered input-sm w-60"
                    disabled={isPreview}
                    bind:value={paymentDetails.approvalNumber}
                />
            </div> -->

            <div class="flex flex-col">
                <div class="flex gap-x-4">
                    <p class="w-48">
                        {paymentDetails.typeOfPayment == 2
                            ? "Refunded"
                            : "Received"}:
                    </p>
                    <input
                        type="checkbox"
                        class="toggle toggle-secondary"
                        disabled={isPreview || isPaymentReceivedOrRefunded}
                        bind:checked={isReceived}
                        on:change={() => handleToggleReceived(isReceived)}
                    />
                </div>
                {#if paymentDetails.typeOfPayment == 1 && !isReceived}
                    <p class="text-secondary">
                        Leaving received date empty means that this will be a
                        pending payment.
                    </p>
                {/if}
            </div>

            {#if paymentDetails.typeOfPayment != 2}
                <div class="flex gap-x-4">
                    <p class="w-48">Date Received:</p>

                    <DateInput
                        bind:value={paymentDetails.transactionDate}
                        isDisable={isPreview}
                        showTime
                        wrapperClass="input input-sm px-0 input-bordered w-60"
                        wrapperClassFocus="inputFocus"
                        showAbove
                        maxDate={paymentDetails.receivedDate
                            ? paymentDetails.receivedDate
                            : null}
                    />
                </div>
            {/if}

            <div class="flex gap-x-4">
                <p class="w-48">
                    Date {paymentDetails.typeOfPayment == 2
                        ? "Refunded"
                        : "Reconciled"}:
                </p>
                <!-- Enable update received/refunded date even if payment has set a received/refunded date -->
                <DateInput
                    bind:value={paymentDetails.receivedDate}
                    isDisable={isPreview}
                    showTime
                    wrapperClass="input input-sm px-0 input-bordered w-60"
                    wrapperClassFocus="inputFocus"
                    minDate={getMinDateOfPayment(
                        {
                            receivedDate: recordedPaymentReceivedDate,
                            transactionDate: paymentDetails.transactionDate,
                        },
                        paymentDetails.status,
                    )}
                    showAbove
                    handleInput={handleReceiveDateInput}
                    bind:this={receiveDateRef}
                />
            </div>
        {/if}
    </div>
</NotificationModal>
